import { v4 as uuidv4 } from 'uuid';
import { readAndDecodeKeyValuePairsFromFragment, } from './Oauth2Service';
import sha256 from 'crypto-js/sha256';
import Base64 from 'crypto-js/enc-base64';
import WordArray from 'crypto-js/lib-typedarrays';
const OAUTH2_INPROGRES_FLOW_STORAGE_KEY = 'codebots_inprogress_ouath2';
export default class CodebotsOauth2Service {
    persistenceStore;
    fetchClient;
    configuration;
    constructor(persistenceStore, fetchClient, configuration) {
        this.persistenceStore = persistenceStore;
        this.configuration = configuration;
        this.fetchClient = fetchClient;
    }
    async handleCallbackUrlFragment() {
        const callbackData = this.parseFragmentCallback(window.location.hash);
        const originalConfig = await this.verifyCallbackResult(callbackData);
        const tokens = await this.retrieveAccessToken(GrantType.AuthorizationCode, callbackData.code, originalConfig.redirectUri, originalConfig.codeVerifier);
        const fragmentResult = { ...tokens, state: originalConfig.state };
        return fragmentResult;
    }
    async initialiseOauth2Flow(state, issuer, redirectUri) {
        const config = ConstructCodebotsOauth2FlowSettings(this.configuration);
        // PKCE base64 uri encoded random value
        const codeVerifier = this.base64URLEncodeCryptoWordArray(WordArray.random(32));
        const oauth2FlowConfig = {
            ...config,
            redirectUri,
            issuer,
            nonce: uuidv4(),
            state,
            codeVerifier,
        };
        await this.persistenceStore.setItem(OAUTH2_INPROGRES_FLOW_STORAGE_KEY, oauth2FlowConfig);
        const codeChallenge = this.base64URLEncodeCryptoWordArray(sha256(codeVerifier));
        const stateHash = sha256(JSON.stringify(oauth2FlowConfig)).toString();
        const params = new URLSearchParams({
            client_id: oauth2FlowConfig.clientId,
            redirect_uri: oauth2FlowConfig.redirectUri,
            scope: oauth2FlowConfig.scope,
            response_mode: oauth2FlowConfig.responseMode,
            response_type: oauth2FlowConfig.responseType,
            state: stateHash,
            nonce: oauth2FlowConfig.nonce,
            code_challenge: codeChallenge,
            code_challenge_method: 'S256',
        });
        const authUri = `${oauth2FlowConfig.authoriseUri}?${params.toString()}`;
        window.location.href = authUri;
    }
    async getTokenFromRefreshToken(currentToken) {
        return this.retrieveAccessToken(GrantType.RefreshToken, currentToken.refresh_token, currentToken.redirect_uri);
    }
    base64URLEncodeCryptoWordArray(wordArray) {
        return Base64.stringify(wordArray).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    }
    parseFragmentCallback(fragment) {
        const resultValues = readAndDecodeKeyValuePairsFromFragment(fragment);
        const callbackData = fragmentValuesToCallbackData(resultValues);
        return callbackData;
    }
    async verifyCallbackResult(result) {
        const inProgressOauthConfig = await this.persistenceStore.getItem(OAUTH2_INPROGRES_FLOW_STORAGE_KEY);
        if (!inProgressOauthConfig) {
            throw new Error('Could not find existing oauth flow data.');
        }
        const stateHash = sha256(JSON.stringify(inProgressOauthConfig)).toString();
        if (result.state !== stateHash || result.iss !== inProgressOauthConfig.issuer) {
            throw new Error('Oauth flow state or issuer do not match requested state or issuer');
        }
        await this.persistenceStore.removeItem(OAUTH2_INPROGRES_FLOW_STORAGE_KEY);
        return inProgressOauthConfig;
    }
    accessTokenPromise;
    async retrieveAccessToken(grantType, code, redirectUri, codeVerifier) {
        // A race condition can occur if two requests are sent at the same time
        // because they will both send the same refresh token. The server will reject it.
        // If the oauth service is already retrieving a token, put subsequent requests
        // into a queue to be resolved after the token is retrieved.
        if (!this.accessTokenPromise) {
            this.accessTokenPromise = (async () => {
                const config = ConstructCodebotsOauth2FlowSettings(this.configuration);
                const formParams = new URLSearchParams();
                formParams.append('grant_type', grantType.toString());
                formParams.append(grantType == GrantType.AuthorizationCode ? 'code' : 'refresh_token', code);
                formParams.append('client_id', config.clientId);
                formParams.append('redirect_uri', redirectUri);
                if (codeVerifier) {
                    formParams.append('code_verifier', codeVerifier);
                }
                try {
                    const request = this.fetchClient.post(config.tokenUrl, formParams, {
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    });
                    const response = await request.response.then((r) => r.json());
                    const tokenData = {
                        ...response,
                        token_acquired_unix_timestamp: new Date().getTime() / 1000,
                        redirect_uri: redirectUri,
                    };
                    return tokenData;
                }
                finally {
                    // Unset the promise so that subsequent requests can be sent
                    this.accessTokenPromise = undefined;
                }
            })();
        }
        return this.accessTokenPromise;
    }
}
var GrantType;
(function (GrantType) {
    GrantType["AuthorizationCode"] = "authorization_code";
    GrantType["RefreshToken"] = "refresh_token";
})(GrantType || (GrantType = {}));
export function fragmentValuesToCallbackData(values) {
    const requiredValues = ['state', 'iss', 'code'];
    for (const requiredValue of requiredValues) {
        if (!values[requiredValue]) {
            throw new Error(`Expected ${requiredValue} when passing oauth callback`);
        }
    }
    return {
        state: values['state'],
        iss: values['iss'],
        code: values['code'],
    };
}
const ConstructCodebotsOauth2FlowSettings = (configuration) => {
    const oauth2Config = configuration.oauth2Config;
    const serviceConfig = configuration.serviceConfig;
    return {
        clientId: oauth2Config.clientId,
        authoriseUri: `${serviceConfig.authServiceApiRoot}/oauth2/auth`,
        tokenUrl: `${serviceConfig.authServiceApiRoot}/oauth2/token`,
        scope: 'openid profile email offline_access',
        responseMode: 'fragment',
        responseType: 'code',
    };
};
//# sourceMappingURL=CodebotsOauth2Service.js.map